import { useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OUTCOMES = [
  { value: "booked", label: "Booked", tone: "success" },
  { value: "declined", label: "Declined", tone: "warning" },
  { value: "no_answer", label: "No answer", tone: "muted" },
  { value: "bad_number", label: "Bad number", tone: "danger" },
];

export default function CallLogDialog({ open, onOpenChange, customer, onLogged }) {
  const [outcome, setOutcome] = useState("booked");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setOutcome("booked"); setNote(""); };

  const submit = async () => {
    if (!customer) return;
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/customers/${customer.id || customer.customer_id}/call-log`, {
        outcome,
        note: note.trim() || null,
      });
      toast.success(`Call logged (${OUTCOMES.find((o) => o.value === outcome)?.label || outcome})`);
      reset();
      onOpenChange(false);
      onLogged && onLogged(data.entry);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="call-log-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-medium flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary" /> Log call outcome
          </DialogTitle>
          <DialogDescription>
            {customer ? <>Recording a call outcome for <span className="font-medium">{customer.name || customer.customer_name}</span>.</> : "Customer required."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="outcome">Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger id="outcome" data-testid="call-log-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OUTCOMES.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`call-log-outcome-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="note">Note (optional)</Label>
            <Textarea
              id="note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Called and left voicemail; will try again Friday."
              data-testid="call-log-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="call-log-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="call-log-submit">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
